import {
  Component,
  OnInit,
  AfterViewInit,
  OnDestroy,
  ElementRef,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import flatpickr from 'flatpickr';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from 'src/app/_services/auth.service';
import { CommonService } from 'src/app/_services/common.service';
import { appConfig } from 'src/app/_core/config/app-config.const';
import { DashboardService } from '../../services/dashboard.service';
import {
  ProfessorAnalyticsService,
  OverviewAnalyticsResponse,
  TangramMetricsAnalyticsResponse,
  ContentMetricsAnalyticsResponse,
  ViewerMetricsAnalyticsResponse,
  EventTrackingAnalyticsResponse,
  AiInsightsAnalyticsResponse,
  ReportsExportAnalyticsResponse,
} from './services/professor-analytics.service';
import {
  AnalyticsTab,
  AnalyticsPeriod,
  ProfessorAnalyticsBundle,
  TrendDelta
} from './models/analytics.types';
import {
  ANALYTICS_PERIOD_OPTIONS,
  ANALYTICS_PERIOD_TRENDS
} from './models/period-trends.const';
import { TangramSelectOption } from 'src/app/shared/components/tangram-select/tangram-select.component';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { unlockPremiumComponent } from 'src/app/modules/dashboard/components/unlockPremium/unlockPremium.component';

const VALID_TABS: AnalyticsTab[] = [
  'overview',
  'tangram-performance',
  'content-performance',
  'viewer-metrics',
  'event-tracking',
  'ai-insights',
  'reports-export'
];

// Thin orchestrator: owns route/auth/period state, delegates rendering
// to per-tab components, persists active tab in the URL query string.
@Component({
  selector: 'tangram-professor-analytics-dashboard',
  templateUrl: './professor-analytics-dashboard.component.html',
  styleUrls: ['./professor-analytics-dashboard.component.scss'],
  // Disabled so per-tab and shared component DOM picks up the
  // .professor-analytics-page namespaced styles in this file.
  encapsulation: ViewEncapsulation.None
})
export class ProfessorAnalyticsDashboardComponent implements OnInit, AfterViewInit, OnDestroy {
  pathwayId = '';
  imgPath: string = appConfig.imagePath;
  currentUser: any;
  loading = true;
  error = '';
  unauthorized = false;
  pathwayDetail: any = null;

  activeTab: AnalyticsTab = 'overview';
  selectedPeriod: AnalyticsPeriod = 'This Month';
  readonly periodOptions = ANALYTICS_PERIOD_OPTIONS;

  customStartDate = '';
  customEndDate   = '';
  get isCustomRange(): boolean { return this.selectedPeriod === 'Custom Range'; }

  get periodSelectOptions(): TangramSelectOption[] {
    return this.periodOptions.map(o => ({ value: o, label: o }));
  }

  onPeriodSelect(value: string): void {
    this.selectedPeriod = value as AnalyticsPeriod;
    if (!this.isCustomRange) { this.loadDashboard(); }
  }

  private flatpickrInstance: any = null;

  @ViewChild('dateRangeInput', { static: false })
  set dateRangeInput(el: ElementRef<HTMLInputElement> | undefined) {
    if (el) {
      this.flatpickrInstance = flatpickr(el.nativeElement, {
        mode: 'range',
        dateFormat: 'Y-m-d',
        maxDate: 'today',
        onChange: (selectedDates: Date[]) => {
          if (selectedDates.length === 2) {
            const fmt = (d: Date) => d.toISOString().slice(0, 10);
            this.customStartDate = fmt(selectedDates[0]);
            this.customEndDate   = fmt(selectedDates[1]);
          }
        },
      });
    } else {
      if (this.flatpickrInstance) {
        this.flatpickrInstance.destroy();
        this.flatpickrInstance = null;
      }
    }
  }

  bundle: ProfessorAnalyticsBundle;

  get isFreeUser(): boolean {
    const u = this.currentUser;
    return !!(u && !u.subscriptionActive && u.role !== 'SUPER_USER');
  }

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private authService: AuthService,
    private commonService: CommonService,
    private dashboardService: DashboardService,
    private analyticsService: ProfessorAnalyticsService,
    private el: ElementRef,
    private modalService: NgbModal,
  ) {
    this.currentUser = this.authService.currentUserValue;
    this.bundle = this.analyticsService.buildMockBundle();
  }

  ngOnInit(): void {
    this.commonService.changeView('user-analytics');
    this.pathwayId = this.resolvePathwayIdFromRoute();

    // Restore active tab from URL on first paint AND react to back/forward
    // navigation. Persists across full page refresh.
    //
    // When the user lands here via a plain link (e.g. the header menu's
    // `routerLink="/user-analytics"`) the URL has no `tab` param —
    // stamp the default onto the URL with replaceUrl so we don't add
    // a history entry. This keeps the URL truthful at all times.
    this.route.queryParams.subscribe((params) => {
      const tab = params['tab'] as AnalyticsTab;
      if (tab && VALID_TABS.indexOf(tab) > -1) {
        if (tab !== this.activeTab) {
          this.activeTab = tab;
          setTimeout(() => this.attachDragScroll(), 0);
        }
      } else {
        this.router.navigate([], {
          relativeTo: this.route,
          queryParams: { tab: this.activeTab },
          queryParamsHandling: 'merge',
          replaceUrl: true
        });
      }
    });

    this.loadDashboard();
  }

  ngAfterViewInit(): void {
    this.attachDragScroll();
  }

  ngOnDestroy(): void {
    if (this.flatpickrInstance) {
      this.flatpickrInstance.destroy();
      this.flatpickrInstance = null;
    }
  }

  // ── Period & trends ─────────────────────────────────────────────
  // Tabs read trend deltas off a single map rather than each tab
  // owning its own period→key mapping.
  get trends(): { [key: string]: TrendDelta } {
    return ANALYTICS_PERIOD_TRENDS[this.selectedPeriod] || {};
  }

  // ── Tab navigation with URL persistence ─────────────────────────
  setTab(tab: AnalyticsTab): void {
    if (tab === this.activeTab) { return; }
    if (this.isFreeUser && tab !== 'overview') {
      this.modalService.open(unlockPremiumComponent, {
        centered: true,
        windowClass: 'unlock-premium',
      });
      return;
    }
    this.activeTab = tab;
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { tab },
      queryParamsHandling: 'merge'
    });
    setTimeout(() => this.attachDragScroll(), 0);
  }

  // ── Top-right Export ────────────────────────────────────────────
  // The Export button writes a CSV of whichever tab is currently active.
  // Each tab pushes its latest server payload into the analytics service
  // when its `load()` resolves (`setLatestTabData(tab, data)`), so this
  // method only has to (a) read the cached payload by `activeTab`, and
  // (b) dispatch to the per-tab CSV builder. There is no extra fetch.
  //
  // If the user clicks Export before any tab has loaded data (e.g. on a
  // failed first request), we fall back to a one-line placeholder CSV
  // that says "no data" so the file is still well-formed.
  exportDashboard(): void {
    const tab = this.activeTab;
    let csv = '';
    let filenameSuffix: string = tab;

    switch (tab) {
      case 'overview': {
        const data = this.analyticsService.getLatestTabData<OverviewAnalyticsResponse>('overview');
        csv = data ? this.buildOverviewCsv(data) : this.buildEmptyCsv('Overview');
        filenameSuffix = 'overview';
        break;
      }
      case 'tangram-performance': {
        const data = this.analyticsService.getLatestTabData<TangramMetricsAnalyticsResponse>('tangram-performance');
        csv = data ? this.buildTangramMetricsCsv(data) : this.buildEmptyCsv('Tangram Metrics');
        filenameSuffix = 'tangram-metrics';
        break;
      }
      case 'content-performance': {
        const data = this.analyticsService.getLatestTabData<ContentMetricsAnalyticsResponse>('content-performance');
        csv = data ? this.buildContentMetricsCsv(data) : this.buildEmptyCsv('Content Metrics');
        filenameSuffix = 'content-metrics';
        break;
      }
      case 'viewer-metrics': {
        const data = this.analyticsService.getLatestTabData<ViewerMetricsAnalyticsResponse>('viewer-metrics');
        csv = data ? this.buildViewerMetricsCsv(data) : this.buildEmptyCsv('Viewer Metrics');
        filenameSuffix = 'viewer-metrics';
        break;
      }
      case 'event-tracking': {
        const data = this.analyticsService.getLatestTabData<EventTrackingAnalyticsResponse>('event-tracking');
        csv = data ? this.buildEventTrackingCsv(data) : this.buildEmptyCsv('Event Tracking');
        filenameSuffix = 'event-tracking';
        break;
      }
      case 'ai-insights': {
        const data = this.analyticsService.getLatestTabData<AiInsightsAnalyticsResponse>('ai-insights');
        csv = data ? this.buildAiInsightsCsv(data) : this.buildEmptyCsv('AI Insights — click Generate first');
        filenameSuffix = 'ai-insights';
        break;
      }
      case 'reports-export': {
        const data = this.analyticsService.getLatestTabData<ReportsExportAnalyticsResponse>('reports-export');
        csv = data ? this.buildReportsExportCsv(data) : this.buildEmptyCsv('Reports & Export');
        filenameSuffix = 'reports-export';
        break;
      }
      default: {
        const data = this.analyticsService.getLatestTabData<OverviewAnalyticsResponse>('overview');
        csv = data ? this.buildOverviewCsv(data) : this.buildEmptyCsv('Overview');
        filenameSuffix = 'overview';
        break;
      }
    }

    const period = String(this.selectedPeriod || '').replace(/\s/g, '-').toLowerCase();
    this.downloadCsv(csv, `analytics-${filenameSuffix}-${period}.csv`);
  }

  // ── CSV builders ─────────────────────────────────────────────────

  // Quote a single cell value when it contains a comma, quote, or newline
  // — the only characters that break a CSV column without escaping. Empty
  // / null / undefined values render as a blank cell so empty rows still
  // line up with the header.
  private csvCell(value: any): string {
    if (value === null || value === undefined) { return ''; }
    const s = String(value);
    if (s.indexOf(',') !== -1 || s.indexOf('"') !== -1 || s.indexOf('\n') !== -1) {
      return '"' + s.replace(/"/g, '""') + '"';
    }
    return s;
  }

  private csvRow(cells: any[]): string {
    return cells.map((c) => this.csvCell(c)).join(',');
  }

  // Render a `[delta, unit]` trend pair as a signed string (e.g. "+12%",
  // "-3 m") so the CSV reader can recognise the direction at a glance.
  private formatTrend(trend: TrendDelta | undefined): string {
    if (!trend) { return ''; }
    const sign = trend.delta > 0 ? '+' : (trend.delta < 0 ? '' : '');
    return `${sign}${trend.delta}${trend.unit || ''}`;
  }

  private buildOverviewCsv(data: OverviewAnalyticsResponse): string {
    const lines: string[] = [];
    lines.push(`Tab,Overview`);
    lines.push(`Period,${this.csvCell(this.selectedPeriod)}`);
    lines.push(`Generated,${new Date().toISOString()}`);
    lines.push('');
    lines.push('Headline KPIs');
    lines.push('Metric,Value,Trend');
    lines.push(this.csvRow(['Total Views', data.tangramMetrics.views, this.formatTrend(data.trends && data.trends.views)]));
    lines.push(this.csvRow(['Unique Viewers', data.tangramMetrics.uniqueUsers, this.formatTrend(data.trends && data.trends.uniqueUsers)]));
    lines.push(this.csvRow(['Avg Completion Rate', `${data.tangramMetrics.completionRate}%`, this.formatTrend(data.trends && data.trends.completionRate)]));
    lines.push(this.csvRow(['Avg Time Spent', data.tangramMetrics.avgTimeSpent, this.formatTrend(data.trends && data.trends.avgTimeSpent)]));
    lines.push(this.csvRow([
      'Published Tangrams',
      // Counts every non-draft, non-deleted pathway, matching the KPI tile.
      data.publishedTangramsCount || 0,
      this.formatTrend(data.trends && data.trends.myTangrams),
    ]));
    lines.push('');
    // Note: the per-tangram list is not included here on purpose. The
    // /overview endpoint no longer ships `myTangrams[]` (the Overview tab
    // doesn't render it), and the Tangram Metrics tab's CSV is the
    // canonical export for that list.
    lines.push('Top Performers');
    lines.push('Title,Viewers,Views,Completion %,Avg Rating');
    (data.topPerformers || []).forEach((row) => {
      lines.push(this.csvRow([row.title, row.students, row.views, row.completionRate, row.avgRating]));
    });
    lines.push('');
    lines.push('Needs Attention');
    lines.push('Title,Viewers,Views,Completion %,Avg Rating');
    (data.needsAttention || []).forEach((row) => {
      lines.push(this.csvRow([row.title, row.students, row.views, row.completionRate, row.avgRating]));
    });
    return lines.join('\n');
  }

  private buildTangramMetricsCsv(data: TangramMetricsAnalyticsResponse): string {
    const lines: string[] = [];
    lines.push(`Tab,Tangram Metrics`);
    lines.push(`Period,${this.csvCell(this.selectedPeriod)}`);
    lines.push(`Generated,${new Date().toISOString()}`);
    lines.push('');
    lines.push('Headline KPIs');
    lines.push('Metric,Value,Trend');
    lines.push(this.csvRow(['Total Views', data.tangramMetrics.views, this.formatTrend(data.trends && data.trends.views)]));
    lines.push(this.csvRow(['Unique Viewers', data.tangramMetrics.uniqueUsers, this.formatTrend(data.trends && data.trends.uniqueUsers)]));
    lines.push(this.csvRow(['Avg Completion Rate', `${data.tangramMetrics.completionRate}%`, this.formatTrend(data.trends && data.trends.completionRate)]));
    lines.push(this.csvRow(['Avg Time Spent', data.tangramMetrics.avgTimeSpent, this.formatTrend(data.trends && data.trends.avgTimeSpent)]));
    lines.push('');
    lines.push('All My Published Tangrams');
    lines.push('Title,Subject,Media Types,Viewers,Views,Completion %,Avg Rating,Status');
    (data.myTangrams || []).forEach((row) => {
      lines.push(this.csvRow([
        row.title,
        row.subject || '',
        (row.mediaTypes || []).join(' / '),
        row.students,
        row.views,
        row.completionRate,
        row.avgRating,
        row.status,
      ]));
    });
    lines.push('');
    lines.push('Top Tangrams');
    lines.push('Title,Viewers,Views,Completion %,Avg Rating');
    (data.topTangrams || []).forEach((row) => {
      lines.push(this.csvRow([row.title, row.students, row.views, row.completionRate, row.avgRating]));
    });
    lines.push('');
    lines.push('Lowest Tangrams');
    lines.push('Title,Viewers,Views,Completion %,Avg Rating');
    (data.lowTangrams || []).forEach((row) => {
      lines.push(this.csvRow([row.title, row.students, row.views, row.completionRate, row.avgRating]));
    });
    return lines.join('\n');
  }

  private buildContentMetricsCsv(data: ContentMetricsAnalyticsResponse): string {
    const lines: string[] = [];
    const filterTitle = data.filter && data.filter.pathwayId
      ? ((data.tangrams || []).find((t) => t.pathwayId === data.filter.pathwayId) || { title: 'Selected Tangram' }).title
      : 'All Tangrams';
    lines.push(`Tab,Content Metrics`);
    lines.push(`Period,${this.csvCell(this.selectedPeriod)}`);
    lines.push(`Tangram Filter,${this.csvCell(filterTitle)}`);
    lines.push(`Generated,${new Date().toISOString()}`);
    lines.push('');
    lines.push('Summary');
    lines.push('Metric,Value');
    lines.push(this.csvRow(['Total Media', data.summary.totalMedia]));
    lines.push(this.csvRow(['Total Views', data.summary.totalViews]));
    lines.push(this.csvRow(['Avg Completion Rate', `${data.summary.avgCompletionRate}%`]));
    lines.push(this.csvRow(['Avg Rating', data.summary.avgRating]));
    lines.push('');
    lines.push('Media Performance');
    lines.push('Title,Type,Pathway,Views,Completion %,Avg Time,Rating,Interactions,Drop-off');
    (data.contentMetrics || []).forEach((row) => {
      lines.push(this.csvRow([
        row.title,
        row.type,
        row.pathwayTitle,
        row.views,
        row.completionRate,
        row.avgTimeSpent,
        row.rating,
        row.interactions,
        row.dropOffPoint,
      ]));
    });
    lines.push('');
    lines.push('Media Type Rollup');
    lines.push('Type,Media Count,Total Views,Avg Completion %,Avg Rating');
    (data.mediaTypePerformance || []).forEach((row) => {
      lines.push(this.csvRow([row.type, row.mediaCount, row.totalViews, row.avgCompletionRate, row.avgRating]));
    });
    lines.push('');
    lines.push('Top Performing Content');
    lines.push('Title,Type,Pathway,Views,Completion %,Rating');
    (data.topPerformingContent || []).forEach((row) => {
      lines.push(this.csvRow([row.title, row.type, row.pathwayTitle, row.views, row.completionRate, row.rating]));
    });
    lines.push('');
    lines.push('Low Performing Content');
    lines.push('Title,Type,Pathway,Views,Completion %,Rating');
    (data.lowPerformingContent || []).forEach((row) => {
      lines.push(this.csvRow([row.title, row.type, row.pathwayTitle, row.views, row.completionRate, row.rating]));
    });
    return lines.join('\n');
  }

  private buildViewerMetricsCsv(data: ViewerMetricsAnalyticsResponse): string {
    const lines: string[] = [];
    const filterTitle = data.filter && data.filter.pathwayId
      ? ((data.tangrams || []).find((t) => t.pathwayId === data.filter.pathwayId) || { title: 'Selected Tangram' }).title
      : 'All Tangrams';
    lines.push(`Tab,Viewer Metrics`);
    lines.push(`Period,${this.csvCell(this.selectedPeriod)}`);
    lines.push(`Tangram Filter,${this.csvCell(filterTitle)}`);
    lines.push(`Generated,${new Date().toISOString()}`);
    lines.push('');
    lines.push('Classroom Metrics');
    lines.push('Metric,Value,Trend');
    lines.push(this.csvRow(['Active Viewers %', `${data.classroomMetrics.activeStudentsPct}%`, this.formatTrend(data.trends && data.trends.activeStudents)]));
    lines.push(this.csvRow(['Avg Completion / Tangram', `${data.classroomMetrics.avgCompletionPerTangram}%`, this.formatTrend(data.trends && data.trends.avgCompletion)]));
    lines.push(this.csvRow(['Avg Time / Tangram', data.classroomMetrics.avgTimePerTangram, this.formatTrend(data.trends && data.trends.avgTimeTangram)]));
    lines.push(this.csvRow(['Avg Time / Content', data.classroomMetrics.avgTimePerContent, this.formatTrend(data.trends && data.trends.avgTimeContent)]));
    lines.push('');
    lines.push('Engagement Metrics');
    lines.push('Metric,Value,Trend');
    lines.push(this.csvRow(['Comments / Tangram', data.engagementMetrics.commentsPerTangram, this.formatTrend(data.trends && data.trends.comments)]));
    lines.push(this.csvRow(['Poll Completion %', `${data.engagementMetrics.pollCompletionRate}%`, this.formatTrend(data.trends && data.trends.pollCompletion)]));
    lines.push(this.csvRow(['Question Completion %', `${data.engagementMetrics.questionCompletionRate}%`, this.formatTrend(data.trends && data.trends.questionCompletion)]));
    lines.push('');
    lines.push('Per-User Progress');
    lines.push('User,Tangrams Viewed,Avg Time / Tangram,Completion %,Comments,Polls Answered,Ratings Given,AI Chat Sessions');
    (data.userEngagement || []).forEach((row) => {
      lines.push(this.csvRow([
        row.user,
        row.tangramsViewed,
        row.avgTimePerTangram,
        row.completionRate,
        row.commentsAdded,
        row.pollsAnswered,
        row.ratingsGiven,
        row.aiChatSessions,
      ]));
    });
    lines.push('');
    lines.push('Top Discussion Themes');
    lines.push('Author,Replies,Comment');
    (data.topThemes || []).forEach((row) => {
      lines.push(this.csvRow([row.name, row.evidence, row.description]));
    });
    lines.push('');
    lines.push('Top AI Inquiries from Users');
    lines.push('Topic,Sample,Count');
    (data.questionInsights || []).forEach((row) => {
      lines.push(this.csvRow([row.topic, row.sample, row.count]));
    });
    return lines.join('\n');
  }

  private buildEventTrackingCsv(data: EventTrackingAnalyticsResponse): string {
    const lines: string[] = [];
    const filterTitle = data.filter && data.filter.pathwayId
      ? ((data.tangrams || []).find((t) => t.pathwayId === data.filter.pathwayId) || { title: 'Selected Tangram' }).title
      : 'All Tangrams';
    lines.push(`Tab,Event Tracking`);
    lines.push(`Period,${this.csvCell(this.selectedPeriod)}`);
    lines.push(`Tangram Filter,${this.csvCell(filterTitle)}`);
    lines.push(`Generated,${new Date().toISOString()}`);
    lines.push('');
    lines.push('Summary');
    lines.push('Metric,Value');
    lines.push(this.csvRow(['Total Events', data.summary.totalEvents]));
    lines.push(this.csvRow(['Total Unique Users', data.summary.totalUniqueUsers]));
    lines.push(this.csvRow(['Events Tracked', data.summary.eventsTracked]));
    lines.push('');
    lines.push('Event Log');
    lines.push('Event,Total Count,Unique Users,Avg / Metric,Last Triggered');
    (data.eventTrackingLog || []).forEach((row) => {
      lines.push(this.csvRow([
        row.event,
        row.count === null ? '' : row.count,
        row.uniqueUsers,
        row.avgValue || '',
        row.lastTriggered,
      ]));
    });
    return lines.join('\n');
  }

  private buildAiInsightsCsv(data: AiInsightsAnalyticsResponse): string {
    const lines: string[] = [];
    lines.push(`Tab,AI Insights`);
    lines.push(`Period,${this.csvCell(this.selectedPeriod)}`);
    lines.push(`Provider,${this.csvCell(data.provider || '')}`);
    lines.push(`Generated,${this.csvCell(data.generatedAt || new Date().toISOString())}`);
    lines.push('');
    lines.push('Summary');
    lines.push('Metric,Value');
    lines.push(this.csvRow(['Tangrams Analyzed', data.summary.eligibleTangrams]));
    lines.push(this.csvRow(['Total Views', data.summary.totalViews]));
    lines.push(this.csvRow(['Unique Viewers', data.summary.totalUniqueStudents]));
    lines.push('');
    lines.push('Insights');
    lines.push('Rank,Category,Impact,Insight');
    (data.insights || []).forEach((row) => {
      lines.push(this.csvRow([row.rank, row.category, row.impact, row.insight]));
    });
    return lines.join('\n');
  }

  private buildReportsExportCsv(data: ReportsExportAnalyticsResponse): string {
    const lines: string[] = [];
    lines.push(`Tab,Reports & Export`);
    lines.push(`Period,${this.csvCell(this.selectedPeriod)}`);
    lines.push(`Generated,${new Date().toISOString()}`);
    lines.push('');
    lines.push('Scheduled Email Reports');
    lines.push('Frequency,Recipients,Next Send,Enabled');
    (data.reportSchedules || []).forEach((row) => {
      lines.push(this.csvRow([
        row.frequency,
        row.recipients,
        row.nextSend,
        row.enabled ? 'Yes' : 'No',
      ]));
    });
    if (data.shareSummary) {
      lines.push('');
      lines.push('Share Summary');
      lines.push('Metric,Value');
      lines.push(this.csvRow(['Period', data.shareSummary.periodLabel]));
      lines.push(this.csvRow(['Published Tangrams', data.shareSummary.publishedTangrams]));
      lines.push(this.csvRow(['Total Views', data.shareSummary.totalViews]));
      lines.push(this.csvRow(['Unique Viewers', data.shareSummary.uniqueStudents]));
      lines.push(this.csvRow(['Avg Completion Rate', `${data.shareSummary.avgCompletionRate}%`]));
      lines.push(this.csvRow(['Avg Time Spent', data.shareSummary.avgTimeSpent]));
    }
    return lines.join('\n');
  }

  private buildEmptyCsv(label: string): string {
    return [
      `Tab,${this.csvCell(label)}`,
      `Period,${this.csvCell(this.selectedPeriod)}`,
      `Generated,${new Date().toISOString()}`,
      '',
      'No data available — open the tab and let it finish loading before exporting.',
    ].join('\n');
  }

  private downloadCsv(csv: string, filename: string): void {
    // Prepend a BOM so Excel renders UTF-8 characters (e.g. — / é) correctly
    // when the user double-clicks the file. The Blob is built from a typed
    // array containing the BOM bytes followed by the CSV string so the
    // download bytes are exactly what Excel expects.
    const bom = new Uint8Array([0xEF, 0xBB, 0xBF]);
    const blob = new Blob([bom, csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }

  goBack(): void {
    this.router.navigate(['/home']);
  }

  // ── Loading ─────────────────────────────────────────────────────
  private loadDashboard(): void {
    this.loading = true;
    this.error = '';
    this.unauthorized = false;

    if (!this.pathwayId || this.pathwayId === 'local-pathway') {
      this.loadFromMock();
      return;
    }

    this.dashboardService.getPathwayDetail(this.pathwayId).subscribe(
      (response: any) => {
        const pathway = (response && response.data) || null;
        if (!pathway) {
          this.loadFromMock();
          return;
        }
        this.pathwayDetail = pathway;
        if (!this.analyticsService.canViewProfessorAnalytics(pathway, this.currentUser)) {
          this.unauthorized = true;
          this.loading = false;
          return;
        }
        this.analyticsService.buildBundleFromPathway(pathway, this.pathwayId).subscribe(
          (bundle) => {
            this.bundle = bundle;
            this.loading = false;
          },
          () => this.loadFromMock()
        );
      },
      () => this.loadFromMock()
    );
  }

  private loadFromMock(): void {
    this.bundle = this.analyticsService.buildMockBundle();
    this.pathwayDetail = {
      _id: this.pathwayId || 'demo',
      title: 'My Published Tangrams',
      description: 'Aggregated analytics across all Tangrams you have created and made public for viewers.',
      createdBy: this.currentUser && this.currentUser._id ? this.currentUser._id : 'demo'
    };
    this.loading = false;
  }

  // ── Helpers ─────────────────────────────────────────────────────
  private resolvePathwayIdFromRoute(): string {
    let current: ActivatedRoute | null = this.route;
    while (current) {
      const paramId = current.snapshot && current.snapshot.params ? current.snapshot.params.pathwayId : '';
      if (paramId) { return paramId; }
      current = current.parent;
    }
    const fromStorage = localStorage.getItem('pathwayId');
    if (fromStorage) { return fromStorage; }
    const match = (this.router.url || '').match(/dashboard\/([^/]+)/);
    return match && match[1] ? match[1] : 'local-pathway';
  }

  private attachDragScroll(): void {
    const host = this.el.nativeElement as HTMLElement;
    const selectors = ['.dash-tabs-scroll', '.table-responsive'];

    selectors.forEach((selector) => {
      host.querySelectorAll<HTMLElement>(selector).forEach((el) => {
        if ((el as any).__dragScrollAttached) { return; }
        (el as any).__dragScrollAttached = true;

        let isDown = false;

        el.addEventListener('mousedown', (e: MouseEvent) => {
          isDown = true;
          el.classList.add('is-dragging');
          e.preventDefault();
        });

        const stop = () => { isDown = false; el.classList.remove('is-dragging'); };
        document.addEventListener('mouseup', stop);
        el.addEventListener('mouseleave', stop);

        el.addEventListener('mousemove', (e: MouseEvent) => {
          if (!isDown) { return; }
          e.preventDefault();
          el.scrollLeft -= e.movementX;
        });
      });
    });
  }
}
